import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { RefreshCw, FileText, Trash2, AlertCircle } from 'lucide-react';
import { ordersService, OrderWithLinesDto, UpdateOrderLineRequest } from '../../../../services/ordersService';
import { BackButton } from '../../../common/BackButton';
import { useWarehouse } from '../../../../contexts/WarehouseContext';

// Import the reusable warehouse table system
import { WarehouseTable, createColumnFactory } from '../../../shared/table';
import type { ColumnDefinition, TableDataRow, RowAction } from '../../../shared/table';

// Editable field names for order lines (matches keys in UpdateOrderLineRequest)
type EditableLineTextField =
  | 'orderLineFreeField1' | 'orderLineFreeField2' | 'orderLineFreeField3'
  | 'orderLineFreeField4' | 'orderLineFreeField5' | 'orderLineFreeField6'
  | 'orderLineFreeField7' | 'orderLineFreeField8' | 'orderLineFreeField9'
  | 'orderLineFreeField10';
type EditableLineNumberField = 'weight' | 'height' | 'length' | 'width' | 'volum';
type EditableLineField = EditableLineTextField | EditableLineNumberField;

// Extended type for table - flattened order line with order header info
interface OrderLineTableRow extends TableDataRow {
  id: string; // Composite key: orderNo-lineNo
  lineDbId: number; // Database ID needed for delete API
  pListID: number | null; // PList binding - null/0 = unbound

  // Order header fields
  orderNo: number;
  orderNoERP: string;
  orderStatus: number;
  orderStatusText: string;
  orderStatusERP: string | null;
  orderType: string | null;
  pickListNoERP: string | null;
  orderDate: string | null;
  customerNo: string;
  customerName: string | null;
  customerType: string | null;
  customerGrp1: string | null;
  customerGrp2: string | null;
  customerGrp3: string | null;
  delAddressNo: number | null;
  delCustomerName: string | null;
  delAddress1: string | null;
  delAddress2: string | null;
  delAddress3: string | null;
  delZipCode: string | null;
  delCity: string | null;
  delCountryCode: string | null;
  delCountryName: string | null;
  deliveryGLN: string | null;
  customerOrderNo: string | null;
  yourRef: string | null;
  ourRef: string | null;
  projectNo: string | null;
  depNo: string | null;
  deliveryMethod: string | null;
  temsOfDelivery: string | null;
  shipmentType: string | null;
  carrierName: string | null;
  termsOfPayment: string | null;
  employee: string | null;
  orderPriority: number | null;
  customerPriority: number | null;
  district: string | null;
  contactName: string | null;
  orderTotal: number | null;
  emailAddress: string | null;
  orderDeliveryYear: number | null;
  orderDeliveryMonth: number | null;
  orderDeliveryDay: number | null;
  customerDeliveryDay: string | null;
  orderIsRemainderYesNo: boolean | null;
  orderCreateRemainderYesNo: boolean | null;
  orderFreeField1: string | null;
  orderFreeField2: string | null;
  orderFreeField3: string | null;
  orderFreeField4: string | null;
  orderFreeField5: string | null;
  orderFreeField6: string | null;

  // Order line fields
  lineNo: number;
  lineNoERP: number | null;
  subLineNoERP: number | null;
  lineStatus: number;
  lineStatusText: string;
  itemNo: string;
  itemBarCode: string | null;
  description1: string | null;
  itemGroup1: string | null;
  itemGroup2: string | null;
  itemGroup3: string | null;
  quantity: number | null;
  quantityDelivered: number | null;
  quantityReserved: number | null;
  quantityPicked: number | null;
  itemUnitCode: string | null;
  itemUnitTxt: string | null;
  price: number | null;
  discount: number | null;
  lineDeliveryDate: string | null;
  serialNoItemYesNo: boolean | null;
  stdLocationName: string | null;
  pickZone: string | null;
  warehouse: string | null;
  batchID: string | null;
  expirationDate: string | null;
  weight: number | null;
  height: number | null;
  length: number | null;
  width: number | null;
  volum: number | null;
  itemPictureName: string | null;
  pListBatchYesNo: boolean | null;
  orderLineFreeField1: string | null;
  orderLineFreeField2: string | null;
  orderLineFreeField3: string | null;
  orderLineFreeField4: string | null;
  orderLineFreeField5: string | null;
  orderLineFreeField6: string | null;
  orderLineFreeField7: string | null;
  orderLineFreeField8: string | null;
  orderLineFreeField9: string | null;
  orderLineFreeField10: string | null;
  lineCreateRemainderYesNo: boolean | null;
  lineIsRemainderYesNo: boolean | null;
  isDisplayPallet: boolean | null;
  qtyOnPallet: number | null;
  printSTAND005: boolean | null;
  itemTemperatureInfo: string | null;
  stackingWeight: number | null;
  unCode: string | null;
}

// Create column factory for type-safe column definitions
const columnFactory = createColumnFactory<OrderLineTableRow>();

interface OrderLinesProps {
  onBack?: () => void;
}

const OrderLines: React.FC<OrderLinesProps> = ({ onBack }) => {
  const { currentWarehouse } = useWarehouse();
  const [orders, setOrders] = useState<OrderWithLinesDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Delete state
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number; label: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Inline editing state (keyed by composite "orderNo-lineNo")
  const [editState, setEditState] = useState<Record<string, Partial<Record<EditableLineField, string | number>>>>({});
  const [savingLines, setSavingLines] = useState<Set<string>>(new Set());
  const [savedLines, setSavedLines] = useState<Set<string>>(new Set());
  const editStateRef = useRef(editState);
  const ordersRef = useRef(orders);

  // Keep refs in sync
  useEffect(() => { editStateRef.current = editState; }, [editState]);
  useEffect(() => { ordersRef.current = orders; }, [orders]);

  // Save a single field inline edit via API
  const saveLineInlineEdit = useCallback(async (
    orderNo: number,
    lineNo: number,
    field: EditableLineField,
    newFieldValue?: string | number
  ) => {
    const rowKey = `${orderNo}-${lineNo}`;
    const orderWithLines = ordersRef.current.find(o => o.header.orderNo === orderNo);
    if (!orderWithLines) return;
    const line = orderWithLines.lines.find(l => l.lineNo === lineNo);
    if (!line) return;

    const editData = editStateRef.current[rowKey];
    let newValue: string | number | undefined;

    if (newFieldValue !== undefined) {
      newValue = newFieldValue;
    } else if (editData) {
      newValue = editData[field];
    } else {
      return;
    }

    // Check if value actually changed
    const oldValue = (line as any)[field] ?? '';
    if (String(newValue) === String(oldValue)) return;

    try {
      setSavingLines(prev => new Set(prev).add(rowKey));

      // Build partial update request with only the changed field
      const request: UpdateOrderLineRequest = {};
      (request as any)[field] = newValue === '' ? null : newValue;

      await ordersService.updateOrderLine(orderNo, lineNo, request);

      // Update local state
      setOrders(prev => prev.map(o => {
        if (o.header.orderNo !== orderNo) return o;
        return {
          ...o,
          lines: o.lines.map(l => {
            if (l.lineNo !== lineNo) return l;
            return { ...l, [field]: newValue === '' ? null : newValue };
          })
        };
      }));

      // Show saved indicator
      setSavedLines(prev => new Set(prev).add(rowKey));
      setTimeout(() => {
        setSavedLines(prev => {
          const newSet = new Set(prev);
          newSet.delete(rowKey);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      console.error('Feil ved lagring:', err);
      alert(`Feil ved lagring: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setSavingLines(prev => {
        const newSet = new Set(prev);
        newSet.delete(rowKey);
        return newSet;
      });
    }
  }, []);

  // Generic renderer for editable text cells (order line)
  const renderEditableLineText = useCallback((field: EditableLineTextField) => {
    return (value: string | null, row: OrderLineTableRow) => {
      const rowKey = row.id;
      const orderNo = row.orderNo;
      const lineNo = row.lineNo;
      const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
      const isSaving = savingLines.has(rowKey);
      const isSaved = savedLines.has(rowKey);

      return (
        <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
          <input
            key={`${field}-${rowKey}-${value}`}
            type="text"
            defaultValue={value || ''}
            onChange={(e) => {
              e.stopPropagation();
              setEditState(prev => ({
                ...prev,
                [rowKey]: { ...prev[rowKey], [field]: e.target.value }
              }));
            }}
            onBlur={() => saveLineInlineEdit(orderNo, lineNo, field)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                saveLineInlineEdit(orderNo, lineNo, field);
                (e.target as HTMLInputElement).blur();
              }
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onFocus={stopAll}
            disabled={isSaving}
            className={`w-full px-2 py-1 text-xs border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
              isSaving ? 'opacity-50' : ''
            } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
          />
          {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
        </div>
      );
    };
  }, [savingLines, savedLines, saveLineInlineEdit]);

  // Generic renderer for editable number cells (order line)
  const renderEditableLineNumber = useCallback((field: EditableLineNumberField) => {
    return (value: number | null, row: OrderLineTableRow) => {
      const rowKey = row.id;
      const orderNo = row.orderNo;
      const lineNo = row.lineNo;
      const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
      const isSaving = savingLines.has(rowKey);
      const isSaved = savedLines.has(rowKey);

      return (
        <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
          <input
            key={`${field}-${rowKey}-${value}`}
            type="number"
            step="any"
            defaultValue={value ?? ''}
            onChange={(e) => {
              e.stopPropagation();
              const numVal = e.target.value === '' ? '' : parseFloat(e.target.value);
              setEditState(prev => ({
                ...prev,
                [rowKey]: { ...prev[rowKey], [field]: numVal }
              }));
            }}
            onBlur={() => saveLineInlineEdit(orderNo, lineNo, field)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                saveLineInlineEdit(orderNo, lineNo, field);
                (e.target as HTMLInputElement).blur();
              }
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onFocus={stopAll}
            disabled={isSaving}
            className={`w-full px-2 py-1 text-xs text-right border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
              isSaving ? 'opacity-50' : ''
            } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
          />
          {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
        </div>
      );
    };
  }, [savingLines, savedLines, saveLineInlineEdit]);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ordersService.getNewOrdersWithLines(currentWarehouse || undefined);
      setOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Feil ved lasting av ordrelinjer');
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  // Delete order line handler
  const handleDeleteOrderLine = useCallback(async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await ordersService.deleteOrderLine(deleteConfirm.id);
      setDeleteConfirm(null);
      await loadOrders();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Kunne ikke slette ordrelinje');
    } finally {
      setDeleting(false);
    }
  }, [deleteConfirm]);

  // Row actions for order line table
  const orderLineRowActions: RowAction<OrderLineTableRow>[] = useMemo(() => [
    {
      id: 'delete-line',
      label: 'Slett ordrelinje',
      icon: Trash2,
      variant: 'danger' as const,
      disabled: (row: OrderLineTableRow) => !!(row.pListID && row.pListID > 0),
      onClick: (row: OrderLineTableRow) => {
        setDeleteConfirm({
          id: row.lineDbId,
          label: `ordrelinje ${row.lineNo} (${row.itemNo}) på ordre ${row.orderNoERP}`
        });
      }
    }
  ], []);

  // Transform orders to flattened order line table format
  const tableData: OrderLineTableRow[] = useMemo(() => {
    const lines: OrderLineTableRow[] = [];

    orders.forEach(orderWithLines => {
      const h = orderWithLines.header;

      orderWithLines.lines.forEach(line => {
        lines.push({
          id: `${h.orderNo}-${line.lineNo}`,
          lineDbId: line.id,
          pListID: line.pListID ?? null,

          // Order header fields
          orderNo: h.orderNo,
          orderNoERP: h.orderNoERP,
          orderStatus: h.orderStatus,
          orderStatusText: h.statusText,
          orderStatusERP: h.orderStatusERP || null,
          orderType: h.orderType || null,
          pickListNoERP: h.pickListNoERP || null,
          orderDate: h.orderDate || null,
          customerNo: h.customerNo,
          customerName: h.customerName || null,
          customerType: h.customerType || null,
          customerGrp1: h.customerGrp1 || null,
          customerGrp2: h.customerGrp2 || null,
          customerGrp3: h.customerGrp3 || null,
          delAddressNo: h.delAddressNo || null,
          delCustomerName: h.delCustomerName || null,
          delAddress1: h.delAddress1 || null,
          delAddress2: h.delAddress2 || null,
          delAddress3: h.delAddress3 || null,
          delZipCode: h.delZipCode || null,
          delCity: h.delCity || null,
          delCountryCode: h.delCountryCode || null,
          delCountryName: h.delCountryName || null,
          deliveryGLN: h.deliveryGLN || null,
          customerOrderNo: h.customerOrderNo || null,
          yourRef: h.yourRef || null,
          ourRef: h.ourRef || null,
          projectNo: h.projectNo || null,
          depNo: h.depNo || null,
          deliveryMethod: h.deliveryMethod || null,
          temsOfDelivery: h.temsOfDelivery || null,
          shipmentType: h.shipmentType || null,
          carrierName: h.carrierName || null,
          termsOfPayment: h.termsOfPayment || null,
          employee: h.employee || null,
          orderPriority: h.orderPriority ?? null,
          customerPriority: h.customerPriority ?? null,
          district: h.district || null,
          contactName: h.contactName || null,
          orderTotal: h.orderTotal ?? null,
          emailAddress: h.emailAddress || null,
          orderDeliveryYear: h.orderDeliveryYear ?? null,
          orderDeliveryMonth: h.orderDeliveryMonth ?? null,
          orderDeliveryDay: h.orderDeliveryDay ?? null,
          customerDeliveryDay: h.customerDeliveryDay || null,
          orderIsRemainderYesNo: h.isRemainderYesNo ?? null,
          orderCreateRemainderYesNo: h.createRemainderYesNo ?? null,
          orderFreeField1: h.orderFreeField1 || null,
          orderFreeField2: h.orderFreeField2 || null,
          orderFreeField3: h.orderFreeField3 || null,
          orderFreeField4: h.orderFreeField4 || null,
          orderFreeField5: h.orderFreeField5 || null,
          orderFreeField6: h.orderFreeField6 || null,

          // Order line fields
          lineNo: line.lineNo,
          lineNoERP: line.lineNoERP ?? null,
          subLineNoERP: line.subLineNoERP ?? null,
          lineStatus: line.lineStatus,
          lineStatusText: line.statusText,
          itemNo: line.itemNo,
          itemBarCode: line.itemBarCode || null,
          description1: line.description1 || null,
          itemGroup1: line.itemGroup1 || null,
          itemGroup2: line.itemGroup2 || null,
          itemGroup3: line.itemGroup3 || null,
          quantity: line.quantity ?? null,
          quantityDelivered: line.quantityDelivered ?? null,
          quantityReserved: line.quantityReserved ?? null,
          quantityPicked: line.quantityPicked ?? null,
          itemUnitCode: line.itemUnitCode || null,
          itemUnitTxt: line.itemUnitTxt || null,
          price: line.price ?? null,
          discount: line.discount ?? null,
          lineDeliveryDate: line.deliveryDate || null,
          serialNoItemYesNo: line.serialNoItemYesNo ?? null,
          stdLocationName: line.stdLocationName || null,
          pickZone: line.pickZone || null,
          warehouse: line.warehouse || null,
          batchID: line.batchID || null,
          expirationDate: line.expirationDate || null,
          weight: line.weight ?? null,
          height: line.height ?? null,
          length: line.length ?? null,
          width: line.width ?? null,
          volum: line.volum ?? null,
          itemPictureName: line.itemPictureName || null,
          pListBatchYesNo: line.pListBatchYesNo ?? null,
          orderLineFreeField1: line.orderLineFreeField1 || null,
          orderLineFreeField2: line.orderLineFreeField2 || null,
          orderLineFreeField3: line.orderLineFreeField3 || null,
          orderLineFreeField4: line.orderLineFreeField4 || null,
          orderLineFreeField5: line.orderLineFreeField5 || null,
          orderLineFreeField6: line.orderLineFreeField6 || null,
          orderLineFreeField7: line.orderLineFreeField7 || null,
          orderLineFreeField8: line.orderLineFreeField8 || null,
          orderLineFreeField9: line.orderLineFreeField9 || null,
          orderLineFreeField10: line.orderLineFreeField10 || null,
          lineCreateRemainderYesNo: null, // Not in DTO - add if needed
          lineIsRemainderYesNo: null, // Not in DTO - add if needed
          isDisplayPallet: line.isDisplayPallet ?? null,
          qtyOnPallet: line.qtyOnPallet ?? null,
          printSTAND005: line.printSTAND005 ?? null,
          itemTemperatureInfo: line.itemTemperatureInfo || null,
          stackingWeight: line.stackingWeight ?? null,
          unCode: line.unCode || null
        });
      });
    });

    return lines;
  }, [orders]);

  // Custom row ID function
  const getRowId = useCallback((row: OrderLineTableRow) => row.id, []);

  // Define columns for order lines table - based on legacy OrderLine.dfm
  const columns: ColumnDefinition<OrderLineTableRow>[] = useMemo(() => [
    // === ORDRELINJE-FELT (hovedkolonner) ===
    columnFactory.createTextColumn('orderNoERP', 'orderNoERP', 'Ordrenr.', {
      width: 100,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createNumberColumn('lineNo', 'lineNo', 'Linjenr. (int)', {
      width: 90,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createNumberColumn('lineNoERP', 'lineNoERP', 'Linjenr. ERP', {
      width: 90,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createNumberColumn('subLineNoERP', 'subLineNoERP', 'Sublinjenr. ERP', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('lineStatusText', 'lineStatusText', 'Linjestatus', {
      width: 110,
      sortable: true,
      filterType: 'dropdown',
      renderer: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value}
        </span>
      )
    }),
    columnFactory.createTextColumn('itemNo', 'itemNo', 'Varenr.', {
      width: 120,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('itemBarCode', 'itemBarCode', 'GTIN', {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('description1', 'description1', 'Varebeskrivelse', {
      width: 250,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createTextColumn('itemGroup1', 'itemGroup1', 'Varegrp. 1', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('itemGroup2', 'itemGroup2', 'Varegrp. 2', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('itemGroup3', 'itemGroup3', 'Varegrp. 3', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('quantity', 'quantity', 'Ant. best.', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => ordersService.formatQuantity(value)
    }),
    columnFactory.createNumberColumn('quantityDelivered', 'quantityDelivered', 'Ant. tidl. levert', {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => ordersService.formatQuantity(value),
      visible: false
    }),
    columnFactory.createNumberColumn('quantityReserved', 'quantityReserved', 'Ant. res.', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => ordersService.formatQuantity(value),
      visible: false
    }),
    columnFactory.createNumberColumn('quantityPicked', 'quantityPicked', 'Ant. plukket', {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => ordersService.formatQuantity(value)
    }),
    columnFactory.createTextColumn('itemUnitCode', 'itemUnitCode', 'Enh. kode', {
      width: 80,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('itemUnitTxt', 'itemUnitTxt', 'Enhet', {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createCurrencyColumn('price', 'price', 'Pris', {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => ordersService.formatCurrency(value)
    }),
    columnFactory.createNumberColumn('discount', 'discount', 'Rabatt', {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createDateColumn('lineDeliveryDate', 'lineDeliveryDate', 'Leveringsdato', {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('serialNoItemYesNo', 'serialNoItemYesNo', 'Serienr. vare', {
      width: 100,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? 'Ja' : value === false ? 'Nei' : '-',
      visible: false
    }),
    columnFactory.createTextColumn('stdLocationName', 'stdLocationName', 'Lokasjon', {
      width: 100,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('pickZone', 'pickZone', 'Plukksone', {
      width: 90,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('warehouse', 'warehouse', 'Lagersted', {
      width: 90,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('batchID', 'batchID', 'Batchnr.', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createDateColumn('expirationDate', 'expirationDate', 'Batchdato', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('weight', 'weight', 'Vekt', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineNumber('weight')
    }),
    columnFactory.createNumberColumn('height', 'height', 'Høyde', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineNumber('height')
    }),
    columnFactory.createNumberColumn('length', 'length', 'Lengde', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineNumber('length')
    }),
    columnFactory.createNumberColumn('width', 'width', 'Bredde', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineNumber('width')
    }),
    columnFactory.createNumberColumn('volum', 'volum', 'Volum', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineNumber('volum')
    }),
    columnFactory.createTextColumn('itemPictureName', 'itemPictureName', 'Bildenavn', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('pListBatchYesNo', 'pListBatchYesNo', 'Samplukk tillatt', {
      width: 110,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? 'Ja' : value === false ? 'Nei' : '-',
      visible: false
    }),

    // Ordrelinje frifelt
    columnFactory.createTextColumn('orderLineFreeField1', 'orderLineFreeField1', 'Linje frifelt 1', {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField1')
    }),
    columnFactory.createTextColumn('orderLineFreeField2', 'orderLineFreeField2', 'Linje frifelt 2', {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField2')
    }),
    columnFactory.createTextColumn('orderLineFreeField3', 'orderLineFreeField3', 'Linje frifelt 3', {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField3')
    }),
    columnFactory.createTextColumn('orderLineFreeField4', 'orderLineFreeField4', 'Linje frifelt 4', {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField4')
    }),
    columnFactory.createTextColumn('orderLineFreeField5', 'orderLineFreeField5', 'Linje frifelt 5', {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField5')
    }),
    columnFactory.createTextColumn('orderLineFreeField6', 'orderLineFreeField6', 'Linje frifelt 6', {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField6')
    }),
    columnFactory.createTextColumn('orderLineFreeField7', 'orderLineFreeField7', 'Linje frifelt 7', {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField7')
    }),
    columnFactory.createTextColumn('orderLineFreeField8', 'orderLineFreeField8', 'Linje frifelt 8', {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField8')
    }),
    columnFactory.createTextColumn('orderLineFreeField9', 'orderLineFreeField9', 'Linje frifelt 9', {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField9')
    }),
    columnFactory.createTextColumn('orderLineFreeField10', 'orderLineFreeField10', 'Linje frifelt 10', {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField10')
    }),

    // Displaypall og SSCC
    columnFactory.createTextColumn('isDisplayPallet', 'isDisplayPallet', 'Displaypall', {
      width: 100,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? 'Ja' : value === false ? 'Nei' : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('qtyOnPallet', 'qtyOnPallet', 'Ant. på pall', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('printSTAND005', 'printSTAND005', 'Generer SSCC', {
      width: 100,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? 'Ja' : value === false ? 'Nei' : '-',
      visible: false
    }),
    columnFactory.createTextColumn('itemTemperatureInfo', 'itemTemperatureInfo', 'Temperaturkrav', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('stackingWeight', 'stackingWeight', 'Stablevekt', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('unCode', 'unCode', 'Varekode', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // === ORDRE-HODE-FELT ===
    columnFactory.createNumberColumn('orderNo', 'orderNo', 'Ordrenr. (int)', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderStatusText', 'orderStatusText', 'Ordrestatus', {
      width: 110,
      sortable: true,
      filterType: 'dropdown',
      visible: false
    }),
    columnFactory.createTextColumn('pickListNoERP', 'pickListNoERP', 'Plukklistenr.', {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderType', 'orderType', 'Ordretype', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderStatusERP', 'orderStatusERP', 'Ordrestatus (ERP)', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createDateColumn('orderDate', 'orderDate', 'Ordredato', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerNo', 'customerNo', 'Kundenr.', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerName', 'customerName', 'Kundenavn', {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('delAddressNo', 'delAddressNo', 'Lev. adr. nr', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delCustomerName', 'delCustomerName', 'Lev. kundenavn', {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delAddress1', 'delAddress1', 'Lev. adr. 1', {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delAddress2', 'delAddress2', 'Lev. adr. 2', {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delAddress3', 'delAddress3', 'Lev. adr. 3', {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delZipCode', 'delZipCode', 'Lev. postnr.', {
      width: 90,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delCity', 'delCity', 'Lev. poststed', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delCountryCode', 'delCountryCode', 'Lev. landkode', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delCountryName', 'delCountryName', 'Lev. landnavn', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('deliveryGLN', 'deliveryGLN', 'Lev. GLN', {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerOrderNo', 'customerOrderNo', 'Oppr. bestnr.', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('yourRef', 'yourRef', 'Deres ref.', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('ourRef', 'ourRef', 'Vår ref.', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('projectNo', 'projectNo', 'Prosjekt', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('depNo', 'depNo', 'Avdeling', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('deliveryMethod', 'deliveryMethod', 'Leveringsmåte', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('temsOfDelivery', 'temsOfDelivery', 'Leveringsbetingelser', {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('shipmentType', 'shipmentType', 'Forsendelsesmåte', {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('carrierName', 'carrierName', 'Transportør', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('termsOfPayment', 'termsOfPayment', 'Betalingsbetingelser', {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('employee', 'employee', 'Medarbeider', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('orderPriority', 'orderPriority', 'Ordreprioritet', {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('customerPriority', 'customerPriority', 'Kundeprioritet', {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('district', 'district', 'Distrikt', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('contactName', 'contactName', 'Kontaktperson', {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createCurrencyColumn('orderTotal', 'orderTotal', 'Ordresum', {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => ordersService.formatCurrency(value),
      visible: false
    }),
    columnFactory.createTextColumn('customerType', 'customerType', 'Kundetype', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerGrp1', 'customerGrp1', 'Kundegrp. 1', {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerGrp2', 'customerGrp2', 'Kundegrp. 2', {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerGrp3', 'customerGrp3', 'Kundegrp. 3', {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderIsRemainderYesNo', 'orderIsRemainderYesNo', 'Er restordre', {
      width: 100,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? 'Ja' : value === false ? 'Nei' : '-',
      visible: false
    }),
    columnFactory.createTextColumn('orderCreateRemainderYesNo', 'orderCreateRemainderYesNo', 'Lag restordre', {
      width: 100,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? 'Ja' : value === false ? 'Nei' : '-',
      visible: false
    }),
    columnFactory.createTextColumn('emailAddress', 'emailAddress', 'E-post', {
      width: 180,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('orderDeliveryYear', 'orderDeliveryYear', 'Lev. år', {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('orderDeliveryMonth', 'orderDeliveryMonth', 'Lev. mnd', {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('orderDeliveryDay', 'orderDeliveryDay', 'Lev. dag', {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerDeliveryDay', 'customerDeliveryDay', 'Kundeleveringsdag', {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // Ordre frifelt
    columnFactory.createTextColumn('orderFreeField1', 'orderFreeField1', 'Ordre frifelt 1', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderFreeField2', 'orderFreeField2', 'Ordre frifelt 2', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderFreeField3', 'orderFreeField3', 'Ordre frifelt 3', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderFreeField4', 'orderFreeField4', 'Ordre frifelt 4', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderFreeField5', 'orderFreeField5', 'Ordre frifelt 5', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderFreeField6', 'orderFreeField6', 'Ordre frifelt 6', {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // Linjestatus kode (skjult - lineStatusText vises i stedet)
    columnFactory.createNumberColumn('lineStatus', 'lineStatus', 'Linjestatus (kode)', {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    })
  ], [renderEditableLineText, renderEditableLineNumber]);

  // Table actions
  const tableActions = useMemo(() => [
    {
      id: 'refresh',
      label: 'Oppdater',
      icon: RefreshCw,
      onClick: () => loadOrders(),
      variant: 'secondary' as const
    }
  ], []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="animate-spin" size={20} />
          <span>Laster ordrelinjer...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
        <h3 className="text-red-800 font-medium">Feil ved lasting av ordrelinjer</h3>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={loadOrders}
          className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
        >
          <RefreshCw size={16} />
          <span>Prøv igjen</span>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-4 pb-2 bg-white border-b">
        <div className="flex items-center space-x-4">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <FileText size={24} />
            <span>Ordrelinjer</span>
          </h1>
          <span className="text-sm text-gray-500">
            {tableData.length} linjer fra {orders.length} ordre
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 px-4 py-2">
        <WarehouseTable
          data={tableData}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: false,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            allowRowSelection: false,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'order-lines-table'
          }}
          getRowId={getRowId}
          tableActions={tableActions}
          rowActions={orderLineRowActions}
          loading={false}
          error={undefined}
          aria-label="Ordrelinjer tabell"
        />
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b flex items-center space-x-3">
              <AlertCircle className="text-red-600" size={24} />
              <h2 className="text-lg font-bold text-gray-900">Bekreft sletting</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-700">
                Er du sikker på at du vil slette {deleteConfirm.label}?
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end space-x-3 rounded-b-lg">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                Avbryt
              </button>
              <button
                onClick={handleDeleteOrderLine}
                disabled={deleting}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="animate-spin" size={16} />
                    <span>Sletter...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    <span>Slett</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderLines;
